import { Link } from 'react-router-dom'
import FaqBlock from '../components/FaqBlock.jsx'
import JumpNav from '../components/JumpNav.jsx'
import Related from '../components/Related.jsx'
import Seo from '../components/Seo.jsx'
import LastUpdated from '../components/LastUpdated.jsx'
import { faqLd } from '../lib/faq.js'

const GUIDE_FAQ = [
  {
    q: 'What is the difference between micrograms (mcg) and milligrams (mg)?',
    a: '1 milligram (mg) equals 1,000 micrograms (mcg). Vials are usually labelled in mg while doses are often written in mcg, so converting both to the same unit is the first thing any calculation does — and mixing them up is a 1,000× error.',
  },
  {
    q: 'Does adding more or less bacteriostatic water change my dose?',
    a: 'No. Water only changes the concentration — and therefore how many units you draw — not the amount of peptide in each dose. More water means you draw more units for the same dose, which makes small doses easier to measure.',
  },
  {
    q: 'How often should I administer my peptide?',
    a: 'It depends on the specific peptide and its half-life. Some are dosed several times a day, others once daily or weekly. Follow the schedule your prescriber or the manufacturer specifies rather than a generic rule.',
  },
  {
    q: 'What should I do if I miss a dose?',
    a: 'Follow the guidance from your healthcare provider or the manufacturer. Generally you either take it when you remember or skip it and resume your normal schedule — you do not double up to make up for a missed dose.',
  },
]

function Section({ id, title, children }) {
  return (
    <section className="article-section" id={id}>
      <h2>{title}</h2>
      {children}
    </section>
  )
}

const JUMP = [
  { id: 'formula', label: 'The formula' },
  { id: 'example', label: 'Worked example' },
  { id: 'units', label: 'mcg vs mg' },
  { id: 'water', label: 'How much water' },
  { id: 'solvents', label: 'What to mix with' },
  { id: 'blends', label: 'Blends' },
  { id: 'mistakes', label: 'Mistakes' },
  { id: 'storage', label: 'Storage' },
]

export default function DosageGuide() {
  return (
    <main className="page">
      <Seo
        title="Peptide Dosing & Dosage Guide — Reconstitution Made Simple"
        description="A practical peptide dosing guide with a worked example: the one dosage formula you need, how bacteriostatic water changes your units, mcg vs mg, and the mistakes to avoid."
        jsonLd={faqLd('Peptide dosage guide — FAQ', GUIDE_FAQ)}
        image="/og/guide.png"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Guides', path: '/guides' },
          { name: 'Dosage Guide', path: '/peptide-dosage-guide' },
        ]}
      />
      <div className="container page-narrow article">
        <span className="pill blue" style={{ marginBottom: 10 }}>Guide</span>
        <h1 className="section-title">Peptide Dosage: How to Reconstitute &amp; Dose Correctly</h1>
        <p className="section-sub" style={{ maxWidth: '64ch' }}>
          Reconstituting a peptide comes down to one relationship between three numbers: how much peptide is in the
          vial, how much water you add, and the dose you want. Get those straight and the “units to draw” falls out
          automatically. Here’s exactly how it works, with a worked example.
        </p>
        <LastUpdated />

        <div className="guide-callout" style={{ margin: '4px 0 10px' }}>
          🧮 <span>Prefer to skip the math? <Link to="/calculator"><strong>Our calculator does it for you →</strong></Link></span>
        </div>

        <JumpNav items={JUMP} />

        <Section id="formula" title="The one formula you need">
          <p>
            After you mix a freeze-dried (<Link to="/glossary">lyophilized</Link>) vial with{' '}
            <Link to="/glossary">bacteriostatic water</Link>, its <strong>concentration</strong> is simply:
          </p>
          <p style={{ fontWeight: 600, color: 'var(--text)' }}>concentration = vial amount (mg) ÷ water added (mL)</p>
          <p>
            To turn a dose into insulin-syringe units, convert everything to the same unit (micrograms), divide the
            dose by the concentration to get the volume, and multiply by 100 — because on a{' '}
            <Link to="/glossary">U-100</Link> syringe, 100 units = 1 mL. That’s the whole thing. The{' '}
            <Link to="/calculator">calculator</Link> runs it instantly, but it helps to see it once.
          </p>
        </Section>

        <Section id="example" title="A worked example: BPC-157">
          <p>
            Say you have a <strong>5 mg</strong> vial of BPC-157 and you want a <strong>250 mcg</strong> dose. Watch
            what happens to the units as you change only the water — the dose never changes:
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table className="pep-table" style={{ minWidth: 380 }}>
              <thead>
                <tr><th scope="col">Water added</th><th scope="col">Concentration</th><th scope="col">Draw for 250 mcg</th></tr>
              </thead>
              <tbody>
                <tr><td>1 mL</td><td>5 mg/mL</td><td><strong>5 units</strong></td></tr>
                <tr><td>2 mL</td><td>2.5 mg/mL</td><td><strong>10 units</strong></td></tr>
                <tr><td>3 mL</td><td>1.67 mg/mL</td><td><strong>15 units</strong></td></tr>
              </tbody>
            </table>
          </div>
          <p>
            All three deliver the exact same 250 mcg. More water just spreads that dose across more units, which
            makes it easier to measure accurately on the syringe. A 5 mg vial at 250 mcg also gives you{' '}
            <strong>20 doses</strong> (5,000 mcg ÷ 250), whatever water you chose.
          </p>
        </Section>

        <Section id="units" title="mcg vs mg — the mistake that matters most">
          <p>
            Vials are labelled in milligrams; doses are frequently written in micrograms. Since{' '}
            <strong>1 mg = 1,000 mcg</strong>, confusing the two is a thousand-fold error — the difference between a
            250 mcg dose and a 250 mg one. Before you calculate anything, make sure the dose unit you enter matches
            what you actually intend. The calculator lets you pick mcg or mg per field for exactly this reason.
          </p>
        </Section>

        <Section id="water" title="How much water should I add?">
          <p>
            It’s your choice, and it doesn’t change your dose — only how many units you draw. <strong>1–3 mL</strong>{' '}
            is typical. Two practical rules of thumb:
          </p>
          <ul className="pep-list">
            <li><strong>Small doses → more water.</strong> If a dose comes out to only 1–2 units, add more water so it lands on a larger, easier-to-read number.</li>
            <li><strong>Watch the syringe limit.</strong> If a dose needs more units than your syringe holds (e.g. over 100 on a 1 mL barrel), use less water or a larger syringe. The calculator flags this automatically.</li>
          </ul>
        </Section>

        <Section id="solvents" title="What to reconstitute with">
          <p>
            For almost all peptides, the right solvent is{' '}
            <Link to="/glossary/bacteriostatic-water">bacteriostatic water</Link> — sterile water with 0.9% benzyl
            alcohol. That preservative is what lets you enter the vial repeatedly over several weeks without it
            spoiling, which is why it’s the default for multi-dose peptide vials.
          </p>
          <ul className="pep-list">
            <li><strong>Bacteriostatic water</strong> — the standard choice; good for roughly 28 days refrigerated once mixed.</li>
            <li><strong>Sterile water</strong> — no preservative, so it’s meant for single use; a mixed vial should be used quickly rather than stored for weeks.</li>
            <li><strong>Dilute acetic acid</strong> — occasionally used first for peptides that don’t fully dissolve in water, then diluted with bacteriostatic water to the working volume. Follow the specific peptide’s handling notes.</li>
          </ul>
          <p className="soft">
            Add the water slowly against the vial wall and let the powder dissolve on its own — don’t shake it hard.
            Whatever you use, the <em>volume</em> you add is what sets your concentration, so measure it.
          </p>
        </Section>

        <Section id="blends" title="Blends and stacks">
          <p>
            Some vials contain <strong>more than one peptide</strong> premixed in the same water — a “blend,” like
            BPC-157/TB-500. Because they share the water, every unit you draw contains a fixed ratio of each
            compound, so you can’t dose them separately. Our <Link to="/blend">blend calculator</Link> shows exactly
            how much of each peptide is in a given draw.
          </p>
        </Section>

        <Section id="mistakes" title="Common mistakes to avoid">
          <ul className="pep-list warn-list">
            <li><strong>Sloppy reconstitution.</strong> Adding a different amount of water than you calculated for changes the concentration and throws off every dose. Measure the water.</li>
            <li><strong>Unit mix-ups.</strong> mcg vs mg, and insulin “units” vs millilitres — keep them straight.</li>
            <li><strong>Ignoring the syringe cap.</strong> A dose that needs 150 units won’t fit a 100-unit syringe; re-mix with less water.</li>
            <li><strong>Contamination.</strong> Swab the stopper with alcohol and don’t touch needle tips — see <Link to="/glossary">aseptic technique</Link>.</li>
          </ul>
        </Section>

        <Section id="storage" title="Storing a reconstituted vial">
          <p>
            Lyophilized (freeze-dried) powder is stable and can sit in the freezer until you need it. Once you’ve
            added water, though, the clock starts. A reconstituted vial is generally kept{' '}
            <strong>refrigerated (about 2–8&nbsp;°C)</strong> and used within roughly <strong>28 days</strong>,
            though the exact window varies by peptide.
          </p>
          <ul className="pep-list">
            <li><strong>Refrigerate after mixing</strong> — don’t leave a reconstituted vial at room temperature for long.</li>
            <li><strong>Don’t freeze the reconstituted liquid</strong> — repeated freeze-thaw cycles can degrade many peptides. Freeze the dry powder, not the solution.</li>
            <li><strong>Keep it out of direct light</strong>, and swab the stopper with alcohol before each draw.</li>
            <li><strong>Label the vial</strong> with the contents, concentration, and the date you reconstituted it, so you can track the 28-day window.</li>
          </ul>
        </Section>

        <FaqBlock title="Peptide dosage FAQ" items={GUIDE_FAQ} />

        <Section title="Put it into practice">
          <p>
            You don’t have to do any of this by hand. <Link to="/calculator">Open the calculator</Link>, use the{' '}
            <Link to="/guide">guided step-by-step version</Link> if you’re new, reach for the{' '}
            <Link to="/blend">blend calculator</Link> for premixed vials, and keep the{' '}
            <Link to="/glossary">glossary</Link> handy for any term you’re unsure about.
          </p>
        </Section>

        <Related
          title="Related"
          links={[
            { to: '/glossary', label: 'Glossary', desc: 'Every term defined' },
            { to: '/how-to-read-a-coa', label: 'How to read a COA', desc: 'Verify your vial' },
            { to: '/stacks', label: 'Peptide stacks', desc: 'Common combinations' },
            { to: '/blend', label: 'Blend calculator', desc: 'Premixed multi-peptide vials' },
          ]}
        />

        <div className="pep-disclaimer" style={{ marginTop: 22 }}>
          ⚠️ This guide is educational and is <strong>not medical advice</strong>. It does not recommend any specific
          dose, medication, or schedule. Follow the exact dosing your prescriber gave you, and consult a qualified
          healthcare professional with any questions.
        </div>
      </div>
    </main>
  )
}
